import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ── useInView hook ──────────────────────────────────────────── */
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Animated counter ────────────────────────────────────────── */
function useCountUp(end, duration = 1800, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = end / (duration / 16);
    const t = setInterval(() => {
      start = Math.min(start + step, end);
      setVal(Math.floor(start));
      if (start >= end) clearInterval(t);
    }, 16);
    return () => clearInterval(t);
  }, [end, duration, active]);
  return val;
}

/* ── SOS Phone Simulator ─────────────────────────────────────── */
function SOSSimulator() {
  const [phase, setPhase] = useState(0);
  const timers = useRef([]);

  const clearTimers = () => timers.current.forEach(clearTimeout);

  const trigger = () => {
    if (phase === 3) { setPhase(0); return; }
    if (phase !== 0) return;
    clearTimers();
    setPhase(1);
    timers.current = [
      setTimeout(() => setPhase(2), 2000),
      setTimeout(() => setPhase(3), 3800),
    ];
  };

  useEffect(() => () => clearTimers(), []);

  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div className="phone-frame">
      <div className="phone-notch"></div>
      <div className="phone-screen">
        <div className="phone-header">
          <span className="phone-header-brand">RAKSHA</span>
          <span className="phone-header-time">{time}</span>
        </div>

        {/* Phase 0 — Idle */}
        {phase === 0 && (
          <div className="sim-idle">
            <div className="sim-user-card">
              <div className="sim-avatar">S</div>
              <div>
                <span className="sim-user-name">Welcome back, Sarah</span>
                <span className="sim-user-status">
                  <i className="bi bi-shield-fill-check" style={{ fontSize: '0.6rem' }}></i>
                  You are protected
                </span>
              </div>
            </div>
            <div className="sim-sos-area">
              <p className="sim-sos-prompt">TAP TO SIMULATE SOS ALERT</p>
              <button className="sim-sos-btn" onClick={trigger}>
                <i className="bi bi-shield-exclamation"></i>
                SOS
              </button>
              <p className="sim-sos-tap">Hold in emergency · Tap to demo</p>
            </div>
            <div className="sim-gps">
              <span className="sim-gps-dot"></span>
              GPS ACTIVE · DUBLIN 2
            </div>
          </div>
        )}

        {/* Phase 1 — Sending */}
        {phase === 1 && (
          <div className="sim-phase">
            <div className="sim-user-card">
              <div className="sim-avatar">S</div>
              <div>
                <span className="sim-user-name">SOS ACTIVE</span>
                <span className="sim-user-status" style={{ color: '#f39c12' }}>
                  <i className="bi bi-broadcast" style={{ fontSize: '0.6rem' }}></i>
                  Broadcasting alert…
                </span>
              </div>
            </div>
            <div className="sim-card sim-card-orange" style={{ animationDelay: '0s' }}>
              <div className="sim-card-title">🔔 Alert Sent</div>
              <div className="sim-card-body">Notifying 3 verified members in your area…</div>
            </div>
            <div className="sim-card sim-card-orange" style={{ animationDelay: '0.3s', animation: 'slideInUp 0.4s ease 0.3s both' }}>
              <div className="sim-card-title">📍 Location Locked</div>
              <div className="sim-card-body">GPS coordinates captured — Dublin 2, IE</div>
            </div>
          </div>
        )}

        {/* Phase 2 — Accepted */}
        {phase === 2 && (
          <div className="sim-phase">
            <div className="sim-user-card">
              <div className="sim-avatar" style={{ background: 'linear-gradient(135deg, #2ecc71, #27ae60)' }}>✓</div>
              <div>
                <span className="sim-user-name">Nearby member accepted</span>
                <span className="sim-user-status">
                  <i className="bi bi-geo-alt-fill" style={{ fontSize: '0.6rem' }}></i>
                  0.4 km away · En route
                </span>
              </div>
            </div>
            <div className="sim-card sim-card-green">
              <div className="sim-card-title">✅ Responder Found</div>
              <div className="sim-card-body">A verified community member<br />ETA approx. 4 minutes</div>
            </div>
            <div className="sim-card" style={{ background: 'rgba(52,152,219,0.1)', borderLeft: '3px solid #3498db', animation: 'slideInUp 0.4s ease 0.25s both' }}>
              <div className="sim-card-title" style={{ color: '#fff' }}>🚶 Moving to you</div>
              <div className="sim-card-body">Live location tracking active</div>
            </div>
          </div>
        )}

        {/* Phase 3 — Confirmed */}
        {phase === 3 && (
          <div className="sim-confirmed">
            <div className="sim-confirmed-icon">🚑</div>
            <p className="sim-confirmed-title">Help is on the way!</p>
            <p className="sim-confirmed-sub">
              A verified member is on their way.<br />Stay where you are.
            </p>
            <button className="sim-reset-btn" onClick={() => setPhase(0)}>
              <i className="bi bi-arrow-counterclockwise"></i> Reset Simulation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Hero Section ────────────────────────────────────────────── */
function HeroSection() {
  return (
    <section className="hero">
      <div className="hero-body">
        <div className="hero-grid">
          {/* Left: Text */}
          <div>
            <div className="hero-badge">
              <i className="bi bi-shield-fill"></i>
              Community Safety Network
            </div>
            <h1 className="hero-title">
              Your Safety Net,<br />
              <span>Always Nearby</span>
            </h1>
            <p className="hero-sub">
              RAKSHA connects you with identity-verified community members around you.
              One tap triggers an emergency SOS alert — real people respond, real fast.
            </p>
            <div className="hero-ctas">
              <a href="#download" className="hero-btn-primary">
                <i className="bi bi-google-play"></i>
                Download for Android
              </a>
              <Link to="/how-it-works" className="hero-btn-secondary">
                See How It Works <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
            <div className="hero-trust">
              <span className="hero-trust-item"><i className="bi bi-check-circle-fill"></i> Free to use</span>
              <span className="hero-trust-item"><i className="bi bi-check-circle-fill"></i> Admin-verified users</span>
              <span className="hero-trust-item"><i className="bi bi-check-circle-fill"></i> Works offline</span>
            </div>
          </div>

          {/* Right: Phone */}
          <div className="hero-right">
            <div className="hero-phone-wrapper">
              <div className="phone-glow"></div>
              <SOSSimulator />
              <div className="floating-cards">
                <div className="float-card">
                  <div className="float-card-title">🔔 Alert nearby</div>
                  <div className="float-card-body">Sarah K. needs help · 0.3 km</div>
                </div>
                <div className="float-card">
                  <div className="float-card-title">✅ A nearby member responded!</div>
                  <div className="float-card-body">Responder accepted · ETA 4 min</div>
                </div>
                <div className="float-card">
                  <div className="float-card-title">📍 Location updated</div>
                  <div className="float-card-body">GPS coordinates synced</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar inside hero */}
      <StatsBar />
    </section>
  );
}

/* ── Stats Bar ───────────────────────────────────────────────── */
function StatsBar() {
  const [ref, inView] = useInView(0.3);
  const v1 = useCountUp(10,  1400, inView);
  const v2 = useCountUp(100, 1600, inView);

  return (
    <div className="stats-bar" ref={ref}>
      <div className="stats-bar-inner">
        <div className="stat-item">
          <div className="stat-value">{inView ? `${v1}s` : '0s'}</div>
          <div className="stat-label">Avg Response Time</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{inView ? `${v2}%` : '0%'}</div>
          <div className="stat-label">Identity-Verified Community</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">24/7</div>
          <div className="stat-label">Always-On Protection</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">1 Tap</div>
          <div className="stat-label">SOS Activation</div>
        </div>
      </div>
    </div>
  );
}

/* ── Process Section ─────────────────────────────────────────── */
const STEPS = [
  { num: 1, icon: '👆', title: 'Tap SOS', desc: 'Hit the large red SOS button. Your hardware GPS location coordinates are captured instantly.' },
  { num: 2, icon: '📡', title: 'Alert Sent',    desc: 'All verified RAKSHA network members within range receive an instant push notification.' },
  { num: 3, icon: '🙋', title: 'Someone Responds', desc: 'A nearby vetted member accepts the alert and immediately begins moving towards your GPS pin.' },
  { num: 4, icon: '📱', title: "You're Notified", desc: "You instantly receive confirmation — 'Help is on the way!' with your responder's name." },
  { num: 5, icon: '🤝', title: 'Help Arrives',  desc: 'The responder reaches you, offering safe presence. Together, you confirm the alert is resolved.' },
];

function ProcessSection() {
  const [ref, inView] = useInView(0.15);
  return (
    <section className="rk-section rk-section-light" ref={ref}>
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label">The Process</span>
          <h2 className="rk-section-title">From Danger to Safety in Seconds</h2>
          <p className="rk-section-sub">No complicated menus. No waiting on hold. Just one tap.</p>
        </div>
        <div className="process-steps">
          {STEPS.map((s, i) => (
            <div className="step-card" key={s.num} style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.1}s both` } : { opacity: 0 }}>
              <div className="step-number">{s.num}</div>
              <div className="step-icon">{s.icon}</div>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Features Section ────────────────────────────────────────── */
const FEATURES = [
  { icon: 'bi-shield-exclamation', iconClass: 'feature-icon-red',    title: 'One-Tap SOS Trigger',       desc: 'A single tap on the centralized SOS button initiates your emergency broadcast. Locks your coordinates and contacts the local safety network instantly — no typing, no menus.' },
  { icon: 'bi-patch-check-fill',   iconClass: 'feature-icon-blue',   title: 'Identity-Verified Grid',    desc: 'Zero anonymity. Every account requires official government-issued photo ID vetting before approval. Only trusted, real people populate our network.' },
  { icon: 'bi-bell-fill',          iconClass: 'feature-icon-orange', title: 'Real-Time Push Alerts',     desc: 'Powered by Firebase Cloud Messaging. Responders receive emergency push notifications the second you tap, even if their screen is locked.' },
  { icon: 'bi-heart-pulse-fill',   iconClass: 'feature-icon-green',  title: 'Help is on the Way!',       desc: 'Eliminate uncertainty during distress. The moment a volunteer responder accepts, your device updates with their name and ETA. You are never left in isolation.' },
  { icon: 'bi-clock-history',      iconClass: 'feature-icon-purple', title: 'Full Incident Audit Logs',  desc: 'Every safety event is logged securely. Track previous SOS broadcasts, volunteer response times, and logged resolutions in your private history archives.' },
  { icon: 'bi-shield-lock-fill',    iconClass: 'feature-icon-cyan',   title: 'End-to-End Verified Network',  desc: 'Every member is identity-verified by an admin before joining. You only connect with real, vetted people in your community.' },
];

function FeaturesSection() {
  const [ref, inView] = useInView(0.1);
  return (
    <section className="rk-section rk-section-dark" ref={ref}>
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label rk-section-label-light">Features</span>
          <h2 className="rk-section-title rk-section-title-white">Built For Real Emergencies</h2>
          <p className="rk-section-sub rk-section-sub-white">Every tool engineered with one core metric — reducing response time.</p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div className="feature-card" key={f.title} style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.08}s both` } : { opacity: 0 }}>
              <div className={`feature-icon ${f.iconClass}`}>
                <i className={`bi ${f.icon}`}></i>
              </div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Personas ────────────────────────────────────────────────── */
const PERSONAS = [
  {
    name: 'Students',
    color: '#3498db',
    icon: '🎓',
    quote: 'Walking back to my apartment after a late library session in Trinity or UCC. One tap gives me instant security.',
  },
  {
    name: 'Lone Workers',
    color: '#e67e22',
    icon: '🦺',
    quote: 'Working alone in isolated or rural counties like Kerry. Having local vetted neighbours ready to support is invaluable.',
  },
  {
    name: 'Elderly & Vulnerable',
    color: '#9b59b6',
    icon: '🧓',
    quote: 'Providing peace of mind for seniors living independently. Vetted local residents can arrive before official services.',
  },
  {
    name: 'Late Commuters',
    color: '#1abc9c',
    icon: '🌙',
    quote: 'Waiting at secluded bus stops or walking home from late shifts. RAKSHA acts as an active, invisible safety guard.',
  },
];

function PersonasSection() {
  const [ref, inView] = useInView(0.15);
  return (
    <section className="rk-section rk-section-light" ref={ref}>
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label">Community Cohesion</span>
          <h2 className="rk-section-title">Who Uses RAKSHA?</h2>
          <p className="rk-section-sub">Designed for everyday people seeking peace of mind across Ireland.</p>
        </div>
        <div className="personas-grid">
          {PERSONAS.map((p, i) => (
            <div className="persona-card" key={p.name} style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.1}s both` } : { opacity: 0 }}>
              <div className="persona-avatar-wrap" style={{ background: `${p.color}20`, border: `3px solid ${p.color}40` }}>
                <div style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem' }}>
                  {p.icon}
                </div>
              </div>
              <h3 className="persona-name">{p.name}</h3>
              <p className="persona-quote">{p.quote}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ── Trust Section ───────────────────────────────────────────── */
function TrustSection() {
  const [ref, inView] = useInView(0.2);
  return (
    <section className="rk-section rk-section-light" ref={ref}>
      <div className="rk-container">
        <div className="trust-grid">
          {/* SVG Illustration */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <svg width="320" height="320" viewBox="0 0 320 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={inView ? { animation: 'fadeInUp 0.6s ease both' } : { opacity: 0 }}>
              {/* Outer glow rings */}
              <circle cx="160" cy="160" r="150" fill="rgba(231,76,60,0.04)" stroke="rgba(231,76,60,0.1)" strokeWidth="1" />
              <circle cx="160" cy="160" r="120" fill="rgba(231,76,60,0.05)" stroke="rgba(231,76,60,0.12)" strokeWidth="1" />
              {/* Main shield */}
              <path d="M160 50 L230 80 L230 170 Q230 220 160 260 Q90 220 90 170 L90 80 Z" fill="#1a1a2e" stroke="#e74c3c" strokeWidth="3"/>
              <path d="M160 70 L215 95 L215 165 Q215 205 160 240 Q105 205 105 165 L105 95 Z" fill="rgba(231,76,60,0.1)"/>
              {/* Lock icon in center */}
              <rect x="143" y="148" width="34" height="28" rx="5" fill="#e74c3c"/>
              <path d="M151 148 L151 138 Q151 126 160 126 Q169 126 169 138 L169 148" stroke="#e74c3c" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              <circle cx="160" cy="162" r="4" fill="#fff"/>
              {/* Orbiting icons */}
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite' }}>
                <circle cx="80" cy="100" r="22" fill="#1a1a2e" stroke="rgba(231,76,60,0.4)" strokeWidth="1.5"/>
                <text x="80" y="106" textAnchor="middle" fontSize="16">🪪</text>
              </g>
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite 1.2s' }}>
                <circle cx="240" cy="100" r="22" fill="#1a1a2e" stroke="rgba(52,152,219,0.4)" strokeWidth="1.5"/>
                <text x="240" y="106" textAnchor="middle" fontSize="16">📍</text>
              </g>
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite 2.4s' }}>
                <circle cx="80" cy="230" r="22" fill="#1a1a2e" stroke="rgba(46,204,113,0.4)" strokeWidth="1.5"/>
                <text x="80" y="236" textAnchor="middle" fontSize="16">🔔</text>
              </g>
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite 3.6s' }}>
                <circle cx="240" cy="230" r="22" fill="#1a1a2e" stroke="rgba(155,89,182,0.4)" strokeWidth="1.5"/>
                <text x="240" y="236" textAnchor="middle" fontSize="16">👁️</text>
              </g>
            </svg>
          </div>

          {/* Text */}
          <div style={inView ? { animation: 'fadeInUp 0.6s ease 0.2s both' } : { opacity: 0 }}>
            <span className="rk-section-label">Safety Standards</span>
            <h2 className="trust-title">Built on Trust and Verified Identity</h2>
            <p className="trust-sub">
              We take physical security and verification seriously from day one. You cannot access the RAKSHA community
              without strict identity verification, eliminating bad actors and ensuring a safe space for everyone in Ireland.
            </p>
            <div className="trust-bullets">
              {[
                'Admin-reviewed identity documents',
                'Encrypted data storage (Supabase + AWS EU)',
                'No anonymous accounts — ever',
                'Single-device login prevents account sharing',
              ].map(b => (
                <div className="trust-bullet" key={b}>
                  <div className="trust-bullet-icon"><i className="bi bi-check-lg"></i></div>
                  {b}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Download CTA ────────────────────────────────────────────── */
function DownloadSection() {
  return (
    <section className="download-section" id="download">
      <div className="rk-container">
        <span className="download-icon">🛡️</span>
        <h2 className="download-title">Join the RAKSHA Safety Grid</h2>
        <p className="download-sub">
          Download free on Android today. Register, submit your ID, and become part of Ireland's
          community safety network.
        </p>
        <a href="#" className="play-store-btn">
          <i className="bi bi-google-play"></i>
          <div className="play-store-btn-text">
            <small>Get it on</small>
            <strong>Google Play</strong>
          </div>
        </a>
        <p className="download-note">
          iOS version coming soon &nbsp;·&nbsp; Free to download &nbsp;·&nbsp; No subscription
        </p>
      </div>
    </section>
  );
}

/* ── FAQ Section ─────────────────────────────────────────────── */
const FAQS = [
  { q: 'Is RAKSHA a replacement for calling 999?',       a: 'No. RAKSHA complements emergency services — it mobilises nearby community members while you wait for official help. Always call 999/112 in a life-threatening emergency.' },
  { q: 'How long does account approval take?',           a: 'Our administrative team typically reviews and approves accounts within 2–4 hours. You will receive an instant push notification the moment your identity is vetted and your account is active.' },
  { q: 'What ID do I need to register?',                 a: 'Any government-issued photo ID is accepted — passport, driving licence, or National Age Card. Your document is strictly viewed by the RAKSHA admin team for manual review and is never shared.' },
  { q: 'Does the app drain my battery?',                 a: 'No. RAKSHA only uses high-accuracy GPS tracking in the background during an active emergency SOS. During normal standby use, battery consumption is virtually undetectable.' },
  { q: 'Can I use RAKSHA outside Ireland?',              a: 'Currently RAKSHA Ireland is tailored specifically for the Irish safety grid (Dublin, Cork, Galway, Limerick, and rural communities). Expansion beyond Ireland is on our roadmap.' },
  { q: 'What if no one is nearby to respond?',           a: 'The alert remains active and broadcast until a responder accepts or you manually cancel the SOS. We always recommend contacting 999 simultaneously in dangerous situations.' },
];

function FAQSection() {
  const [open, setOpen] = useState(null);
  return (
    <section className="rk-section rk-section-light">
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label">Questions</span>
          <h2 className="rk-section-title">Frequently Asked Questions</h2>
          <p className="rk-section-sub">Clear, direct answers regarding the operations of the safety grid.</p>
        </div>
        <div className="faq-list">
          {FAQS.map((f, i) => (
            <div className={`faq-item${open === i ? ' open' : ''}`} key={i}>
              <button className="faq-question" onClick={() => setOpen(open === i ? null : i)}>
                {f.q}
                <i className="bi bi-chevron-down faq-chevron"></i>
              </button>
              <div className="faq-answer">
                <div className="faq-answer-inner">{f.a}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Home ────────────────────────────────────────────────────── */
export default function Home() {
  return (
    <main>
      <HeroSection />
      <ProcessSection />
      <FeaturesSection />
      <PersonasSection />
      <TrustSection />
      <DownloadSection />
      <FAQSection />
    </main>
  );
}
